import React, { useEffect, useRef, useState } from 'react'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { IconClose, IconDownload } from '../../../../shared/icon/icon'
import { MenuItem } from '../../../../shared/menu/menu'
import { serverMessage } from '../../../../shared/error/server-message'
import { plural } from '../../../../shared/sso-workspace/format'
import { aiToolsAvailable } from './ai-tools-api'
import { KINDS, downloadFolder, fileName, saveBlob } from './ai-tools-download'
import { buildZip } from './ai-tools-zip'
import { EXPORT_JSON } from './ai-tools-folder'
import { matchFilter } from './ai-tools-words'

// "Download" on the Sessions and Postmortems tabs: everything the tab's
// current filters match, as one .zip the browser saves which unzips to the
// folder the tools write locally (owner 2026-10-23; ai-tools-download.js reads
// it page by page through the staff export, with the staff session; the
// Sessions tab's also holds those sessions' postmortems). A progress line and
// Cancel while it runs; Cancel stops after the current page and saves nothing;
// a failure says the server's message and saves nothing. onDone({ filter,
// upTo, count, at }) hands the completed download to the tab, for "Mark all
// matching…" (what a download held).
//
// kind: 'session' | 'postmortem'; filters: the list's filters (listFilters +
// from/to); fromDay, toDay: the chosen local dates (for the file's name);
// label: what the button says ("Download 2 sessions" once the count is
// known; Download by default).
//
// It is the list menu's first pinned item (owner 2026-10-29: the list's actions
// are the shared menu's items, so every header button is the same size and
// style): useDownload answers the item, a Cancel item while it runs, and
// what it has to say for the card's status line under the header.

// How long a download runs before its button becomes Cancel download.
export const SLOW_MS = 600

const megabytes = bytes => `${(bytes / (1024 * 1024)).toFixed(1)} MB`

// title: why the item is held, when it is (disabled). onStart(): a download began.
export const useDownload = ({ kind, filters, fromDay, toDay, disabled, title, label = 'Download', onDone, onStart }) => {
  const spec = KINDS[kind]
  const [running, setRunning] = useState(null)
  const [message, setMessage] = useState(null)
  const [failure, setFailure] = useState(null)
  const cancelled = useRef(false)
  const mounted = useRef(true)
  // Leaving the tab ends a running download: no file is saved, nothing is set.
  useEffect(() => () => { mounted.current = false; cancelled.current = true }, [])

  const start = async () => {
    if (running) return
    cancelled.current = false
    setMessage(null)
    setFailure(null)
    setRunning({ items: 0, bytes: 0 })
    if (onStart) onStart()
    const filter = matchFilter(kind, filters)
    try {
      const result = await downloadFolder({ kind, filter, onProgress: progress => { if (mounted.current) setRunning(progress) }, isCancelled: () => cancelled.current })
      if (!mounted.current) return
      // Cancel wins at any point before the file is saved, the last page's
      // read and the zip included (a Cancel during the last page used to be
      // seen only between pages, and the file was saved anyway).
      const cancelledText = 'The download was cancelled; nothing was saved.'
      if (result.cancelled || cancelled.current) {
        setMessage({ tone: 'plain', text: cancelledText })
        return
      }
      const parts = await buildZip(result.entries, { stored: [EXPORT_JSON] })
      if (!mounted.current) return
      if (cancelled.current) {
        setMessage({ tone: 'plain', text: cancelledText })
        return
      }
      saveBlob(new Blob(parts, { type: 'application/zip' }), fileName(kind, fromDay, toDay))
      const held = kind === 'session' ? `${plural(result.sessions, 'session')} and ${plural(result.postmortems, 'postmortem')}` : plural(result.postmortems, 'postmortem')
      const notes = [`Downloaded ${held} as a zip, in the folders the tools write (sessions/, postmortems/).`]
      if (result.count > 0 && kind === 'session' && result.truncated > 0) {
        notes.push(`${plural(result.truncated, 'session')} ${result.truncated === 1 ? 'was' : 'were'} cut at the export's per-session limit (export.json names them).`)
      }
      if (result.count > 0 && result.unlinked > 0) {
        notes.push(`${plural(result.unlinked, 'record')} ${result.unlinked === 1 ? 'was' : 'were'} replaced by an unlink marker.`)
      }
      setMessage({ tone: 'plain', text: notes.join(' ') })
      if (onDone) onDone({ filter, upTo: result.upTo, count: result.count, at: Date.now() })
    } catch (err) {
      if (!mounted.current) return
      setMessage({ tone: 'bad', text: serverMessage(err, "The download couldn't be made, and nothing was saved.") })
      setFailure(err)
    } finally {
      if (mounted.current) setRunning(null)
    }
  }
  const cancel = () => { cancelled.current = true }

  // A download that is still running after SLOW_MS turns its own button into
  // Cancel download (owner 2026-10-29: a Cancel button appearing beside it, for
  // a download that is over in a moment, made the header jump); a quicker one
  // never changes the header at all.
  const [slow, setSlow] = useState(false)
  useEffect(() => {
    if (!running) { setSlow(false); return undefined }
    const timer = setTimeout(() => setSlow(true), SLOW_MS)
    return () => clearTimeout(timer)
  }, [!!running]) // eslint-disable-line react-hooks/exhaustive-deps

  const available = aiToolsAvailable()
  const items = [
    running && slow
      ? <MenuItem key="download" Icon={IconClose} text="Cancel download" pin keepOnMobile show={available} onClick={cancel} />
      : <MenuItem key="download" Icon={IconDownload} text={label} pin keepOnMobile show={available} disabled={disabled || !!running} title={disabled && !running ? title : undefined} onClick={start} />
  ]
  const notice = !available
    ? null
    : running && !slow
      ? null
      : running
      ? <span role="status" className="sw-hint">{`Downloading… ${plural(running.items, spec.noun)}${kind === 'session' && running.postmortems ? ` and ${plural(running.postmortems, 'postmortem')}` : ''} so far, ${megabytes(running.bytes)}`}</span>
      : message && message.tone === 'bad'
        ? <span className="text-danger" role="alert">{message.text} <ErrorReference error={failure} /></span>
        : message
          ? <span className="sw-hint" role="status">{message.text}</span>
          : null
  return { items, notice, running: !!running }
}
